import { type TFunction } from 'i18next';

import { WHEEL_PRIZES } from '@/entities/wheel/model/wheel.types';

import { formatDuration } from '@/shared/helpers/format-duration';
import { formatNumber } from '@/shared/helpers/format-number';
import { formatToPercentage } from '@/shared/helpers/formatToPercentage';
import { XPIcon } from '@/shared/ui/xp-icon/XPIcon';

// use url instead of inline svg to avoid glitches when animating in safari
import BoosterPlaycoinImage from '@/assets/images/boosters/boost-pdck-background.svg';
import BoosterXPImage from '@/assets/images/boosters/boost-xp-background.svg';
import ClickerIcon from '@/assets/images/clicker-new.svg';
import LuckImage from '@/assets/images/luck.svg';
import PlaycoinPrizeImage from '@/assets/images/playcoin-s.svg';
import PremiumPrizeImage from '@/assets/images/premium.svg';
import TicketPrizeImage from '@/assets/images/ticket-prize.webp';
import NotCoinImage from '@/assets/images/wallet/notcoin.svg';
import TonPrizeImage from '@/assets/images/wallet/ton.svg';

import { type PossiblePrizeType, type SimplePrize } from './types';

export const ICON_BY_TYPE: Record<
  PossiblePrizeType,
  ((prize: SimplePrize) => string) | string | React.ComponentType<{ className?: string }>
> = {
  [WHEEL_PRIZES.PARTNER]: XPIcon,
  [WHEEL_PRIZES.PLAYCOIN]: PlaycoinPrizeImage,
  [WHEEL_PRIZES.TICKET]: TicketPrizeImage,
  [WHEEL_PRIZES.PREMIUM]: PremiumPrizeImage,
  [WHEEL_PRIZES.TON]: TonPrizeImage,
  [WHEEL_PRIZES.NOT]: NotCoinImage,
  [WHEEL_PRIZES.BOOSTER_PLAYCOIN]: BoosterPlaycoinImage,
  [WHEEL_PRIZES.BOOSTER_XP]: BoosterXPImage,
  [WHEEL_PRIZES.CLICKER_PLAYCOIN]: ClickerIcon,
  [WHEEL_PRIZES.CLICKER_XP]: ClickerIcon,
  [WHEEL_PRIZES.XP]: XPIcon,
  [WHEEL_PRIZES.PHYSICAL]: (prize: SimplePrize) => prize.image,
  [WHEEL_PRIZES.LOOTBOX]: (prize: SimplePrize) => prize.image,
  [WHEEL_PRIZES.LUCK]: LuckImage,
};

export const PRIORITY_BY_TYPE: Record<PossiblePrizeType, number> = {
  [WHEEL_PRIZES.PHYSICAL]: 1,
  [WHEEL_PRIZES.PARTNER]: 1,
  [WHEEL_PRIZES.LUCK]: 1,
  [WHEEL_PRIZES.CLICKER_XP]: 1,
  [WHEEL_PRIZES.CLICKER_PLAYCOIN]: 1,

  [WHEEL_PRIZES.TON]: 2,
  [WHEEL_PRIZES.BOOSTER_XP]: 2,

  [WHEEL_PRIZES.TICKET]: 3,
  [WHEEL_PRIZES.PREMIUM]: 3,

  [WHEEL_PRIZES.NOT]: 4,
  [WHEEL_PRIZES.XP]: 4,

  [WHEEL_PRIZES.PLAYCOIN]: 5,
  [WHEEL_PRIZES.BOOSTER_PLAYCOIN]: 5,
  [WHEEL_PRIZES.LOOTBOX]: 5,
};

export const PRIZE_NAME_BY_TYPE: Record<
  PossiblePrizeType,
  (prize: SimplePrize, t: TFunction) => string
> = {
  [WHEEL_PRIZES.PARTNER]: (prize) => prize.name,
  [WHEEL_PRIZES.PLAYCOIN]: (prize, t) => `${formatNumber(prize.amount, t)} $PlayCoin`,
  [WHEEL_PRIZES.TICKET]: (prize, t) =>
    `${formatNumber(prize.amount, t)} ${t('tickets', { count: prize.amount })}`,
  [WHEEL_PRIZES.TON]: (prize, t) => `${formatNumber(prize.amount, t)} TON`,
  [WHEEL_PRIZES.PREMIUM]: ({ name, amount }) => `${formatToPercentage(amount)}\n${name}`,
  [WHEEL_PRIZES.NOT]: (prize, t) => `${formatNumber(prize.amount, t)} NOT`,
  [WHEEL_PRIZES.BOOSTER_PLAYCOIN]: (prize) => prize.name,
  [WHEEL_PRIZES.BOOSTER_XP]: (prize) => prize.name,
  [WHEEL_PRIZES.XP]: (prize) => prize.type,
  [WHEEL_PRIZES.CLICKER_PLAYCOIN]: (prize, t) =>
    t('Tap Yeti for 10 secs', {
      duration: formatDuration(+(prize.durationSeconds ?? '0'), t, { withUnit: true }),
    }),
  [WHEEL_PRIZES.CLICKER_XP]: (prize, t) =>
    t('Tap Yeti for 10 secs', {
      duration: formatDuration(+(prize.durationSeconds ?? '0'), t, { withUnit: true }),
    }),
  [WHEEL_PRIZES.PHYSICAL]: (prize) => prize.name,
  [WHEEL_PRIZES.LOOTBOX]: (prize, t) => t(prize.name),
  [WHEEL_PRIZES.LUCK]: (_, t) => t('Luck'),
};

export const PRIZE_DESCRIPTION_BY_TYPE: Partial<
  Record<PossiblePrizeType, (prize: SimplePrize, t: TFunction) => string>
> = {
  [WHEEL_PRIZES.CLICKER_PLAYCOIN]: (_, t) => t("He'll totally give you XP"),
  [WHEEL_PRIZES.CLICKER_XP]: (_, t) => t('to get $PlayCoin out of him'),
};
